import { useCallback } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import useTranslation from './useTranslation';

type MutationStatus = UseMutationResult['status'];

type ButtonTextConfig = {
  status?: MutationStatus;
  isSubmitting: boolean;
  hasChanges: boolean;
  labels?: {
    saving?: string;
    allSaved?: string;
    noChanges?: string;
    saveChanges?: string;
  };
};

export type SaveButtonIcon = 'spinner' | 'check' | 'save' | 'none';

/**
 * TELV1-607 — the icon that goes with getButtonText's label, derived from the
 * same two inputs so the two can never disagree (the Design page used to
 * pick label and icon from different change signals and could render an
 * empty button).
 */
export const getSaveButtonIcon = (
  status: MutationStatus | undefined,
  hasChanges: boolean,
  isSubmitting = false,
): SaveButtonIcon => {
  // Mirrors getButtonText: "Saving…" whenever pending OR submitting.
  if (status === 'pending' || isSubmitting) return 'spinner';
  if (status === 'success' && !hasChanges) return 'check';
  // "No changes" has never carried an icon.
  if (!hasChanges) return 'none';
  return 'save';
};

export const useFormButtonText = () => {
  const { lang } = useTranslation();

  const getButtonText = useCallback(
    ({ status, isSubmitting, hasChanges, labels = {} }: ButtonTextConfig) => {
      const {
        saving = 'msg.saving',
        allSaved = 'msg.allSaved',
        noChanges = 'msg.noChanges',
        saveChanges = 'msg.saveChanges',
      } = labels;

      if (status === 'pending' || isSubmitting) return lang.get(saving);
      if (status === 'success' && !hasChanges) return lang.get(allSaved);
      if (!hasChanges) return lang.get(noChanges);
      return lang.get(saveChanges);
    },
    [lang],
  );

  return { getButtonText };
};
